import React, { useEffect, useState } from 'react';
// Components
import {
  priceConverter,
  scaleConverter,
  ProgressiveImageLoader,
  BoltIcon,
} from '../../common';
import FlipUnitContainer from '../../common/components/FlipUnitContainer';
import { AddToCartButton } from '../../current-order';
import { ProductSquare } from '../../products';
import { Link } from 'react-router-dom';
// Helpers
import moment from 'moment';
// Types
import type { ScheduledDiscount } from '../types';
// Styles
import './TimedPromotion.css';

type Props = {
  promotion: ScheduledDiscount,
};

const isProductAvailable = product => {
  if (!product || !product.inStock || product.discontinued) {
    return false;
  }
  return true;
};

const TimedPromotion = ({ promotion }: Props) => {
  const [hours, setHours] = useState({
    value: 0,
    shuffle: true,
  });
  const [minutes, setMinutes] = useState({
    value: 0,
    shuffle: true,
  });
  const [seconds, setSeconds] = useState({
    value: 0,
    shuffle: true,
  });

  const changeTimerState = (value: number) => {
    setMinutes(prev => ({
      value,
      shuffle: !prev.shuffle,
    }));
    setHours(prev => ({
      value,
      shuffle: !prev.shuffle,
    }));
    setSeconds(prev => ({
      value,
      shuffle: !prev.shuffle,
    }));
  };

  useEffect(() => {
    const targetTime = moment(promotion.endAt).utcOffset('-0300');
    const updateTime = () => {
      const now = moment().utcOffset('-0300');
      const diff = targetTime.diff(now, 'seconds');
      if (diff <= 0) {
        changeTimerState(0);
        clearInterval(timerInterval);
      } else {
        const remainingHours = Math.floor(diff / 3600);
        const remainingMinutes = Math.floor((diff % 3600) / 60);
        const remainingSeconds = diff % 60;

        setHours(prev => ({
          value: remainingHours,
          shuffle: prev.value !== remainingHours ? !prev.shuffle : prev.shuffle,
        }));

        setMinutes(prev => ({
          value: remainingMinutes,
          shuffle:
            prev.value !== remainingMinutes ? !prev.shuffle : prev.shuffle,
        }));

        setSeconds(prev => ({
          value: remainingSeconds,
          shuffle:
            prev.value !== remainingSeconds ? !prev.shuffle : prev.shuffle,
        }));
      }
    };

    const timerInterval = setInterval(updateTime, 1000);

    return () => clearInterval(timerInterval);
  }, [promotion.endAt]);

  const isTimerExpired = !hours.value && !minutes.value && !seconds.value;
  if (!promotion.endAt || isTimerExpired) return null;

  const availableVariants =
    promotion.variants?.filter(isProductAvailable) || [];
  if (!availableVariants.length) return null;

  const firstVariant = availableVariants[0];
  const discountPercent = promotion.percent
    ? parseInt(promotion.percent, 10)
    : firstVariant && firstVariant.listPrice > firstVariant.price
    ? Math.round(
        ((firstVariant.listPrice - firstVariant.price) /
          firstVariant.listPrice) *
          100,
      )
    : null;

  return (
    <div className="timed-promotion-container">
      {discountPercent ? (
        <div className="timed-promotion-discount-badge">
          <p>{discountPercent}%</p>
          <span>OFF</span>
        </div>
      ) : null}

      <div className="timed-promotion-announcement">
        <h2 className="timed-promotion-announcement-title">
          OFERTA <b>RELÂMPAGO</b>
          <BoltIcon className="timed-promotion-announcement-bolt" />
        </h2>
        <div className="timed-promotion-announcement-timer">
          <span className="timed-promotion-announcement-timer-text">
            Acaba em
          </span>
          <div className="timed-promotion-announcement-timer-countdown">
            <div className="timed-promotion-announcement-timer-countdown-content">
              <FlipUnitContainer
                unit={'hours'}
                digit={hours.value}
                shuffle={hours.shuffle}
              />
              <span>H</span>
            </div>
            <span>:</span>
            <div className="timed-promotion-announcement-timer-countdown-content">
              <FlipUnitContainer
                unit={'minutes'}
                digit={minutes.value}
                shuffle={minutes.shuffle}
              />
              <span>M</span>
            </div>
            <span>:</span>
            <div className="timed-promotion-announcement-timer-countdown-content">
              <FlipUnitContainer
                unit={'seconds'}
                digit={seconds.value}
                shuffle={seconds.shuffle}
              />
              <span>S</span>
            </div>
          </div>
        </div>
      </div>

      <div className="timed-promotion-product">
        {availableVariants.map(product => (
          <div key={product.id} className="timed-promotion-product-variant">
            <div className="timed-promotion-product-content">
              <Link
                to={`/produtos/${product.slug}`}
                className="timed-promotion-product-image-wrapper"
              >
                <div className="timed-promotion-product-image">
                  <ProgressiveImageLoader
                    selectedImage={product?.images && product.images[0]}
                    imageToShow={'product'}
                  />
                </div>
              </Link>
              <div className="timed-promotion-product-details">
                <div className="timed-promotion-product-description">
                  <p className="timed-promotion-product-description-manufacutrer">
                    {product.manufacturer}
                  </p>
                  <Link
                    to={`/produtos/${product.slug}`}
                    className="timed-promotion-product-name-link"
                  >
                    <p className="timed-promotion-product-description-name">
                      {product.name}
                    </p>
                  </Link>
                  {product.description && (
                    <p className="timed-promotion-product-description-details">
                      {product.description}
                    </p>
                  )}
                </div>
                <div className="timed-promotion-product-cost">
                  <div className="timed-promotion-product-price-wrapper">
                    {product.listPrice > product.price && (
                      <span className="timed-promotion-product-old-price">
                        {priceConverter(
                          scaleConverter(
                            product.listPrice,
                            product.unitForQuantity,
                          ),
                        )}
                      </span>
                    )}
                    <span className="timed-promotion-product-current-price">
                      {priceConverter(
                        scaleConverter(product.price, product.unitForQuantity),
                      )}
                      <span className="timed-promotion-product-unit">
                        /{product.unitForQuantity === 'g' ||
                        product.unitForQuantity === 'kg'
                          ? 'Kg'
                          : 'Un'}
                      </span>
                    </span>
                  </div>
                  <AddToCartButton
                    productId={product.id}
                    unitForQuantity={product.unitForQuantity}
                    defaultQuantity={product.defaultQuantity}
                    incrementQuantity={product.incrementQuantity}
                    inStock={product.inStock}
                    discontinued={product.discontinued}
                    analyticsViewSource={'timed promotion'}
                  />
                </div>
              </div>
            </div>
            <div className="timed-promotion-product-square">
              <ProductSquare
                productImage={product?.images && product.images[0]}
                productName={product.name}
                productManufacturer={product.manufacturer}
                productListPrice={product.listPrice}
                productPrice={product.price}
                productUnit={product.unitForQuantity}
                productSlug={product.slug}
                productId={product.id}
                parentProductId={product.productId}
                productTotalOnHand={product.totalOnHand}
                defaultQuantity={product.defaultQuantity}
                incrementQuantity={product.incrementQuantity}
                inStock={product.inStock}
                discontinued={product.discontinued}
                analyticsViewSource="timed promotion"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TimedPromotion;
